import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Leaf,
  Phone,
  Sparkles,
} from "lucide-react";

const ServicePage = ({ service }) => {
  const variants = {
    container: { hidden: {}, show: { transition: { staggerChildren: 0.12 } } },
    fadeUp: {
      hidden: { opacity: 0, y: 18 },
      show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
      },
    },
  };

  const cardVariant = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const Icon = service.icon;

  return (
    <div className="bg-white overflow-x-hidden">
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={variants.container}
        className="relative bg-emerald-950 text-white pt-40 pb-24 px-4 overflow-hidden md:px-24"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${service.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-emerald-950/85 to-emerald-900/70" />

        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div variants={variants.fadeUp} className="lg:col-span-7 space-y-6 text-left">
            <span className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/30 text-lime-400 text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest">
              <Icon size={14} /> Landscaping Service
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-[1.1]">
              {service.title}
            </h1>
            <p className="text-base md:text-lg text-emerald-100/85 max-w-2xl leading-relaxed">
              {service.description}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="tel:+64228980029"
                className="inline-flex items-center gap-2 rounded-xl bg-lime-500 px-8 py-4 text-base font-extrabold text-emerald-950 shadow-lg shadow-lime-500/20 transition hover:bg-lime-600"
              >
                Enquiry Now <ArrowRight size={18} />
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center rounded-xl border border-emerald-700 bg-emerald-900/50 px-8 py-4 text-base font-bold text-white transition hover:bg-emerald-900"
              >
                Get Free Quote
              </a>
            </div>
          </motion.div>

          <motion.div variants={cardVariant} className="lg:col-span-5">
            <div className="rounded-[2.5rem] border-4 border-emerald-800 bg-emerald-900/30 p-4 shadow-2xl">
              <img
                src={service.image}
                alt={service.title}
                className="h-[420px] w-full rounded-[2rem] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </motion.section>

      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start"
      >
        <motion.div variants={cardVariant} className="space-y-6">
          <span className="text-lime-600 font-extrabold uppercase tracking-wider text-xs block">
            What We Deliver
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight leading-tight">
            Professional {service.title} for homes and commercial spaces
          </h2>
          <p className="text-slate-600 leading-relaxed">{service.longText}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            {service.points.map((point) => (
              <div key={point} className="flex items-center gap-3 text-slate-800 font-bold text-sm">
                <CheckCircle2 className="text-lime-500 flex-shrink-0" size={18} />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div variants={cardVariant} className="rounded-[2rem] bg-stone-50 p-8 border border-slate-100 shadow-sm">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-lime-100 text-lime-700 mb-6">
            <Sparkles size={30} />
          </div>
          <h3 className="text-2xl font-black text-emerald-950 mb-4">
            Why choose us for this service?
          </h3>
          <p className="text-slate-600 leading-relaxed mb-8">
            Our team uses reliable tools, careful planning, and clean execution so your outdoor space is practical, polished, and easy to maintain.
          </p>
          <div className="space-y-4">
            {[
              "Free consultation and clear estimate",
              "Experienced Christchurch landscaping team",
              "Residential and commercial support",
              "Quality materials and tidy site handover",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Leaf className="mt-1 text-lime-600 flex-shrink-0" size={18} />
                <span className="font-bold text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.section>

      <section className="bg-emerald-950 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight">
            Ready to start your {service.title} project?
          </h2>
          <p className="text-emerald-100/80 text-lg max-w-2xl mx-auto">
            Call us today and we will help you plan the right solution for your property.
          </p>
          <a
            href="tel:+64228980029"
            className="inline-flex items-center gap-2 rounded-xl bg-lime-500 px-8 py-4 text-base font-extrabold text-emerald-950 transition hover:bg-lime-600"
          >
            <Phone size={18} />
            +64 228 980 029
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
